import {sendEvent} from '@github-ui/hydro-analytics'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {isStaff} from '@github-ui/stats'
import {type FeedbackRef, UserFeedback} from '@github-ui/user-feedback'
import {ArrowSwitchIcon, ArrowUpRightIcon, CommentIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Label, VisuallyHidden} from '@primer/react'
import {useRef} from 'react'

import {
  ISSUE_FEEDBACK_MAX_LENGTH,
  ISSUE_FEEDBACK_RATING_OPTIONS,
  type IssueFeedbackRating,
} from '../IssueFeedbackDialog'

const STAFF_FEEDBACK_URL = 'https://github.com/github/planning-tracking/discussions/4549'
const FEEDBACK_URL = 'https://gh.io/issue-timeline-feedback'
const TURN_OFF_ERROR = 'The Activity timeline preview could not be turned off. Try again.'

export type ActivityTimelineOptOutFeedback = {rating: IssueFeedbackRating | null; reason: string}

type ActivityTimelinePreviewMenuProps = {
  /** Without it, the menu only offers feedback. */
  onTurnOff?: (feedback: ActivityTimelineOptOutFeedback) => Promise<void>
}

export function ActivityTimelinePreviewMenu({onTurnOff}: ActivityTimelinePreviewMenuProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()
  const label = staff ? 'Staff' : 'Preview'
  const triggerRef = useRef<HTMLButtonElement>(null)
  const turnOffDialogRef = useRef<FeedbackRef<IssueFeedbackRating>>(null)

  const turnOff = async (rating: IssueFeedbackRating | null, reason: string): Promise<string[]> => {
    try {
      await onTurnOff?.({rating, reason})
      return []
    } catch {
      return [TURN_OFF_ERROR]
    }
  }

  return (
    <>
      <ActionMenu anchorRef={triggerRef}>
        <ActionMenu.Button ref={triggerRef} variant="invisible" size="small" aria-label={`${label}: timeline options`}>
          <Label variant={staff ? 'accent' : 'success'}>{label}</Label>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem
              href={staff ? STAFF_FEEDBACK_URL : FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => sendEvent('issue_viewer.activity_timeline_preview.feedback_clicked')}
            >
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Give feedback
              <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            {onTurnOff && (
              <>
                <ActionList.Divider />
                <ActionList.Item onSelect={() => turnOffDialogRef.current?.openDialog()}>
                  <ActionList.LeadingVisual>
                    <ArrowSwitchIcon />
                  </ActionList.LeadingVisual>
                  Turn off Activity timeline preview
                </ActionList.Item>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {onTurnOff && (
        <UserFeedback
          ref={turnOffDialogRef}
          title="Turn off Activity timeline preview?"
          description={
            <p className="m-0">
              You&apos;ll go back to the full issue timeline. You can turn it back on with{' '}
              <strong>Try the new Activity timeline</strong> on an issue or from Feature preview. Feedback is optional.
            </p>
          }
          placeholderText="Why are you turning it off?"
          submitLabel="Turn off"
          submitVariant="danger"
          characterLimit={ISSUE_FEEDBACK_MAX_LENGTH}
          required={false}
          options={ISSUE_FEEDBACK_RATING_OPTIONS}
          onSubmit={turnOff}
          returnFocusRef={triggerRef}
        />
      )}
    </>
  )
}
